/*
 * Copyright (c) 2022 Puratos. All rights reserved.
 */

import { PackagingSelector } from './SKUSelector';
import { setProductPageActiveSku } from '../../../actions/productPage';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useI18N } from '../../hoc/withI18N';
import { useDispatch, useSelector } from 'react-redux';
import {
    getActiveSku,
    getActiveSkuFromVariations,
    getActiveSkuNumber,
    getSkus,
} from '../../../selectors/productPage';
import { isLoggedIn } from '../../../data/user.model';

function ProductPackageSelector({
    variations,
    obsolete,
    restrictedView = false,
    noSplitView = false,
    condensed = false,
}) {
    const [initialized, setInitialized] = useState(false);

    const translate = useI18N();
    const dispatch = useDispatch();
    const skus = useSelector(getSkus);
    const activeSKU = useSelector(getActiveSku);
    const activeSkuNumber =
        useSelector(getActiveSkuNumber) ||
        parseInt(variations[0]?.materialNumber);
    const activeVariation =
        useSelector((state) => getActiveSkuFromVariations(state, variations)) ||
        variations[0];

    const applyGrayScale = useCallback((element, isCarousel) => {
        if (!element || !isLoggedIn()) {
            return;
        }

        if (obsolete || activeSKU?.isObsolete || !activeVariation?.orderable) {
            if (
                !element.classList.contains(
                    isCarousel ? 'carousel-overview--sold-out' : 'sold-out'
                )
            ) {
                element.classList.add(
                    isCarousel ? 'carousel-overview--sold-out' : 'sold-out'
                );
            }
        } else {
            if (
                element.classList.contains(
                    isCarousel ? 'carousel-overview--sold-out' : 'sold-out'
                )
            ) {
                element.classList.remove(
                    isCarousel ? 'carousel-overview--sold-out' : 'sold-out'
                );
            }
        }
    }, []);

    useEffect(() => {
        const e = document.querySelector(
            '.product-page__details .carousel-overview, .product-page__details > .container .image'
        );
        const isCarousel =
            (e && e.classList.contains('carousel-overview')) || false;
        applyGrayScale(e, isCarousel);
    }, [obsolete, activeSKU, variations]);

    useEffect(() => {
        if (!initialized) {
            setInitialized(true);
        }

        const packShot = document.querySelector(
            ".p-slick__thumb[data-sku='" +
                activeVariation?.materialNumber +
                "']"
        );

        if (packShot && typeof packShot.click === 'function') {
            packShot.click();
        }
    }, [activeVariation]);

    const selector = (
        <div
            className={
                'product-page__pricing--details-size' +
                (condensed ? ' condensed' : '')
            }
        >
            <span>{translate('Size')}</span>
            {restrictedView || (variations && variations.length > 1) ? (
                <div className={restrictedView ? 'restricted' : ''}>
                    <PackagingSelector
                        skus={skus}
                        variations={variations}
                        selected={activeSkuNumber}
                        onChange={(e) => dispatch(setProductPageActiveSku(e))}
                    />
                    {!condensed && (
                        <span>
                            {translate('{0} available sizes').replace(
                                '{0}',
                                variations?.length
                            )}
                        </span>
                    )}
                </div>
            ) : (
                <div className="size">
                    {variations && variations[0] ? variations[0].packaging : ''}
                </div>
            )}
        </div>
    );

    if (restrictedView) {
        return variations && variations.length > 1 ? (
            <div
                className={
                    'product-page__pricing--details' +
                    (!noSplitView ? ' split-view' : '')
                }
            >
                {selector}
            </div>
        ) : null;
    }

    return selector;
}

export default ProductPackageSelector;
